import api from "../api/Client";

export type ProfessionalCustomerListItem = {
  user_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  created_at: string | null;
  profile_photo: string | null;
  next_booking_date: string | null;
  last_booking_date: string | null;
  booking_count: number;
};

export type ProfessionalCustomerBooking = {
  booking_id: number;
  start_date: string;
  start_time: string | null;
  status: string;
  comment: string | null;
  late: number;
  absent: number;
  created_at: string | null;
  coach_id: number | null;
  service_id: number | null;
  product_id: number | null;
  coach_name: string | null;
  service_name: string | null;
  product_name: string | null;
};

export type ProfessionalCustomerDocument = {
  document_id: number;
  document_name: string;
  document: string;
  created_at: string | null;
};

export type ProfessionalCustomerDetail = {
  user_id: number;
  name: string;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  postal_code: string | null;
  gender: string | null;
  customer_training_factor: string | number | null;
  customer_company: string | null;
  aprende_entrenar: boolean;
  comentario: string | null;
  info_nuevos_clientes: string | null;
  profile_photo: string | null;
  created_at: string | null;
  documents: ProfessionalCustomerDocument[];
  booked_bookings: ProfessionalCustomerBooking[];
  active_bookings: ProfessionalCustomerBooking[];
  completed_bookings: ProfessionalCustomerBooking[];
  canceled_bookings: ProfessionalCustomerBooking[];
};

export type ProfessionalCustomerProduct = {
  id: number;
  active_product_id: number;
  expiry_date: string | null;
  name: string;
  description: string | null;
  price: number | null;
  image: string | null;
  centro: string | null;
  type_of_product: string | null;
  active_product_status: string | null;
  payment_method: string | null;
  payment_status: string | null;
  service_ids: number[];
};

export type ProfessionalService = {
  id: number;
  name: string;
};

export type ProfessionalStatusProduct = {
  active_product_id: number;
  customer_id: number;
  product_name: string;
  type_of_product: string | null;
  purchase_date: string | null;
  expiry_date: string | null;
  centro: string | null;
  active_product_status?: string | null;
  payment_method?: string | null;
  payment_status?: string | null;
  total_session?: number | null;
  used_sessions?: number | null;
  product_id?: number | null;
};

export type ProfessionalProductConsumption = {
  active_product_id: number;
  customer_id: number;
  product_id: number;
  product_name: string;
  type_of_product: string | null;
  purchase_date: string | null;
  expiry_date: string | null;
  centro: string | null;
  total_session: number;
  used_sessions: number;
  last_session_date: string | null;
  consumed_at: string | null;
  consumed: boolean;
  sessions: Array<{
    booking_id: number;
    start_date: string;
    start_time: string | null;
  }>;
};

export type ProfessionalBookingDetail = {
  booking_id: number;
  customer_id: number;
  customer_name: string | null;
  service_name: string | null;
  product_name: string | null;
  type_of_product: string | null;
  product_image: string | null;
  start_date: string;
  start_time: string | null;
  status: string;
  payment_status: string | null;
  payment_method: string | null;
  product_id: number | null;
  service_id: number | null;
  session_timeslot_id: number | null;
  coach_id: number | null;
  coach_name: string | null;
  active_product_id: number | null;
  comment: string | null;
  discount: number | null;
  purchase_date: string | null;
  expiry_date: string | null;
  amount: number | null;
  total_amount: number | null;
};

export async function fetchProfessionalCustomers() {
  const response = await api.get("/api/professionals/customers");
  return (Array.isArray(response.data?.data) ? response.data.data : []) as ProfessionalCustomerListItem[];
}

export async function fetchProfessionalCustomerDetail(customerId: number) {
  const response = await api.get(`/api/professionals/customers/${customerId}`);
  return response.data?.data as ProfessionalCustomerDetail;
}

export async function updateProfessionalCustomerComment(customerId: number, comentario: string | null) {
  await api.patch(`/api/professionals/customers/${customerId}/comment-general`, { comentario });
}

export async function updateProfessionalCustomerInfoNew(customerId: number, info_nuevos_clientes: string | null) {
  await api.patch(`/api/professionals/customers/${customerId}/info-new`, { info_nuevos_clientes });
}

export async function fetchProfessionalCustomerProducts(customerId: number) {
  const response = await api.get(`/api/professionals/customers/${customerId}/products`);
  return (Array.isArray(response.data?.data) ? response.data.data : []) as ProfessionalCustomerProduct[];
}

export async function fetchProfessionalServices() {
  const response = await api.get("/api/professionals/services");
  return (Array.isArray(response.data?.data) ? response.data.data : []) as ProfessionalService[];
}

export async function fetchProfessionalExpiredProducts(customerId: number) {
  const response = await api.get(`/api/professionals/customers/${customerId}/products/expired`);
  return (Array.isArray(response.data?.data) ? response.data.data : []) as ProfessionalStatusProduct[];
}

export async function fetchProfessionalConsumedProducts(customerId: number) {
  const response = await api.get(`/api/professionals/customers/${customerId}/products/consumed-sessions`);
  return (Array.isArray(response.data?.data) ? response.data.data : []) as ProfessionalStatusProduct[];
}

export async function fetchProfessionalProductSessions(customerId: number, productId: number, activeProductId?: number) {
  const response = await api.get(`/api/professionals/customers/${customerId}/products/${productId}/sessions-consumption`, {
    params: activeProductId ? { active_product_id: activeProductId } : undefined
  });
  return (Array.isArray(response.data?.data) ? response.data.data : []) as ProfessionalProductConsumption[];
}

export async function fetchProfessionalBookingDetail(bookingId: number) {
  const response = await api.get(`/api/professionals/customers/bookings/${bookingId}/details`);
  return response.data?.data as ProfessionalBookingDetail;
}

export async function updateProfessionalBookingComment(bookingId: number, comment: string) {
  await api.patch(`/api/professionals/customers/bookings/${bookingId}/comment`, { comment });
}

export async function deleteProfessionalBooking(bookingId: number) {
  await api.delete(`/api/professionals/customers/bookings/${bookingId}`);
}

export async function fetchProfessionalCustomerDocumentBlob(customerId: number, filename: string, inline = false) {
  const response = await api.get(`/api/professionals/customers/${customerId}/documents/${filename}`, {
    params: inline ? { inline: 1 } : undefined,
    responseType: "blob"
  });
  return response.data as Blob;
}
